import { useTranslations } from "@jugl-web/utils";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { useEffect, useState } from "react";
import { SelectTrigger } from "@jugl-web/ui-components/cross-platform/forms/SelectTrigger";
import { Customer } from "@jugl-web/rest-api";
import { ListBoxItem, Button } from "@jugl-web/ui-components";
import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform";

import { AddNewCustomerDialog } from "@web-src/modules/customers/components/AddNewCustomerDialog/AddNewCustomerDialog";
import { useCustomerListBox } from "@jugl-web/domain-resources/users/hooks/useCustomerListBox";
import { useCustomer } from "@jugl-web/domain-resources/customers/hooks/useCustomer";
import { ReactComponent as PlusIcon } from "@web-src/modules/invoices/pages/InvoicesItemsPage/components/InvoicesFilters/FiltersDialog/components/TypeDateFilter/assets/plus.svg";
import { Placement } from "@floating-ui/react";
import { ReactComponent as CustomerIcon } from "./icons/customer.svg";

interface CustomerPickerProps {
  entityId: string;
  onChange: (customer: ListBoxItem<Customer | null> | null) => void;
  value: string | null;
  addButtonLabel?: string;
  triggerType?: "select" | "button";
  placement?: Placement;
  allowAdd?: boolean;
  defaultSelectedIds?: string[];
}

export const CustomerPicker = ({
  entityId,
  onChange,
  value,
  triggerType = "select",
  allowAdd = false,
  addButtonLabel,
  placement = "left-start",
  defaultSelectedIds,
}: CustomerPickerProps) => {
  const [isAddNewOpen, setIsAddNewOpen] = useState(false);
  const { t } = useTranslations();

  const { getListBoxProps, clearSearchQuery, refresh } = useCustomerListBox({
    entityId,
  });

  const { safeCustomerDetails } = useCustomer({
    entityId,
    customerId: value,
  });

  const listBoxProps = getListBoxProps({
    defaultSelectedIds: value ? [value] : undefined,
    hasSearch: true,
  });

  const addButton = {
    label: t({
      id: "tasks-page.add-customer",
      defaultMessage: "Add customer",
    }),
    startIcon: <CustomerIcon />,
    onClick: () => {
      setIsAddNewOpen(true);
    },
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <ResourcePickerPopover
        placement={placement}
        className="w-screen max-w-sm"
        addButton={allowAdd ? addButton : undefined}
        onSelect={({ item, isSelected, onClose }) => {
          onChange(isSelected ? item : null);
          onClose();
        }}
        onUnmount={clearSearchQuery}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => {
          if (triggerType === "button") {
            return (
              <Trigger ref={triggerRef} as={InteractiveContainer}>
                <Button
                  color="grey"
                  className="h-8 px-3 text-sm"
                  iconEnd={<PlusIcon />}
                >
                  {addButtonLabel ||
                    t({ id: "common.add", defaultMessage: "Add" })}
                </Button>
              </Trigger>
            );
          }

          return (
            <Trigger
              ref={triggerRef}
              as={SelectTrigger}
              isOpen={isOpen}
              className="max-w-sm"
              label={
                value
                  ? safeCustomerDetails?.fullName
                  : t({
                      id: "customer-picker.select-customer",
                      defaultMessage: "Select customer",
                    })
              }
              hasValue={Boolean(value)}
            />
          );
        }}
        {...listBoxProps}
        defaultSelectedIds={defaultSelectedIds}
      />

      <AddNewCustomerDialog
        isOpen={isAddNewOpen}
        onClose={() => setIsAddNewOpen(false)}
      />
    </>
  );
};
